import { type ReactNode } from 'react';

import { NavLink, useParams } from 'react-router-dom';

import { useGame } from '@/hooks/useGame';
import { gateFor, type DashboardItem } from '@/models/dashboardItem';
import { type PermissionCode } from '@/models/permission';

import { useSidebarMode } from './appSidebarProvider';
import { GameImage } from '../gameMiniature';
import { PermissionCheck } from '../permissionCheck';
import {
  useSidebar,
  Sidebar as ShadSidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from '../ui/sidebar';

interface ProSidebarProps {
  items?: DashboardItem[];
}

// Scoped to the current route, so the same entry can be hidden on one Sandbox and
// shown on another.
const NavGate = ({
  permissions,
  children,
}: {
  permissions?: PermissionCode[];
  children: ReactNode;
}) =>
  permissions ? (
    <PermissionCheck oneOf={permissions}>{children}</PermissionCheck>
  ) : (
    children
  );

const MenuItemWithChildren = ({ item }: { item: DashboardItem }) => {
  const { isMobile, setOpenMobile } = useSidebar();

  const hasChildren = !!item.items?.length;

  // A Radix Dialog doesn't close on a route change, so without this a tap navigates
  // behind a drawer that stays open over the page.
  const closeDrawer = () => {
    if (isMobile) setOpenMobile(false);
  };

  return (
    <NavGate permissions={gateFor(item)}>
      <SidebarMenuItem>
        {/* The label needs its own span: the icon-mode rule targets
            [&>span:last-child], and a bare text node never collapses. */}
        {/* An entry with children opens a flyout when collapsed (index.css), and
            the stock tooltip would only land on top of it. */}
        <SidebarMenuButton
          asChild
          tooltip={hasChildren ? undefined : item.label}
        >
          <NavLink end to={item.link} onClick={closeDrawer}>
            {item.icon}
            <span>{item.label}</span>
          </NavLink>
        </SidebarMenuButton>
        {hasChildren && (
          <SidebarMenuSub>
            {item.items?.map((_item) => (
              <NavGate key={_item.label} permissions={_item.permissions}>
                <SidebarMenuSubItem>
                  <SidebarMenuSubButton asChild>
                    <NavLink
                      end
                      to={`${item.link}/${_item.link}`}
                      className={({ isActive }) => (isActive ? 'active' : '')}
                      onClick={closeDrawer}
                    >
                      <span>{_item.icon}</span>
                      <span>{_item.label}</span>
                    </NavLink>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              </NavGate>
            ))}
          </SidebarMenuSub>
        )}
      </SidebarMenuItem>
    </NavGate>
  );
};

// useGame throws outside a Game, and the studio dashboard has none.
const GameFooter = () => {
  const game = useGame();

  return (
    // The footer shows as soon as the rail starts widening, so the fixed-size image
    // would spill over the page until the transition ends.
    <SidebarFooter className="overflow-hidden group-data-[collapsible=icon]:hidden">
      <SidebarMenu>
        <SidebarMenuItem>
          <div className="aspect-square size-36 mx-auto grid place-items-center ">
            <GameImage game={game} />
          </div>
        </SidebarMenuItem>
      </SidebarMenu>
    </SidebarFooter>
  );
};

export const AppSidebar = ({ items = [] }: ProSidebarProps) => {
  const { gameCode } = useParams();
  const { onHoverChange } = useSidebarMode();
  const { isMobile } = useSidebar();

  // Sidebar's mobile branch spreads leftover props onto a Radix Dialog.Root, where
  // DOM handlers are inert anyway.
  const hoverProps = isMobile
    ? {}
    : {
        onMouseEnter: () => {
          onHoverChange(true);
        },
        onMouseLeave: () => {
          onHoverChange(false);
        },
      };
  return (
    // The breadcrumb now owns the top of the viewport, so the fixed container has
    // to start below it. Important because the stock inset-y-0 stays on the element
    // (tailwind-merge only drops it the other way round) and would otherwise win or
    // lose on CSS source order.
    <ShadSidebar
      collapsible="icon"
      className="top-(--header-height)! h-[calc(100svh-var(--header-height))]"
      {...hoverProps}
    >
      <SidebarContent>
        <SidebarGroup>
          <SidebarMenu>
            {items.map((item) => (
              <MenuItemWithChildren key={item.label} item={item} />
            ))}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>
      {gameCode && <GameFooter />}
    </ShadSidebar>
  );
};
